import { tinyUUID } from '../util';
/**
 * The inspector is a tool that can be used to debug and inspect fsm state machines.
 * It can hypothetically be used to integrate with different environments, but right
 * now is primarily used to expose a debugger for the console.
 */
export class Inspector {
  constructor({
    debug = false,
    initialStatus = 'initializing',
    maxTimelineSize = 100,
    name,
    sourceFile
  } = {}) {
    this.status = 'initializing';
    this.timelineVersion = 0;
    this.timeline = [];
    this.debug = debug;
    this.status = initialStatus;
    this.maxTimelineSize = maxTimelineSize;
    this.name = name || tinyUUID();
    this.isAnonymous = !name;
    this.sourceFile = sourceFile;
  }

  /**
   * The inspector doesn't initialize the machine until it's explicitly started.
   * This ensures that we wait for the machine that it's inspecting to be ready.
   */
  start() {
    this.status = 'running';
  }

  /**
   * Clear the timeline.
   */
  clear() {
    this.timeline = [];
    this.timelineVersion += 1;
  }

  /**
   * Reset the inspector to its initial state, and set the status to initializing.
   */
  reset() {
    this.clear();
    this.status = 'initializing';
  }

  /**
   * Stop the inspector. This will prevent any further events from being added to the timeline.
   */
  stop() {
    this.status = 'stopped';
  }

  /**
   * A decision is a special event triggered by the internal state machine.
   * It describes a decision that was made by the machine. A decision can
   * be any, but not limited to, one of the following:
   *
   * - A transition that was taken
   * - An error in an action
   * - A guard condition that caused a transition to be skipped or taken
   * - A service resolution that caused a transition
   * - An event that was ignored due to a missing transition or action handler
   *
   * @param {MachineTimelineEntry<TEvent>} entry
   */
  add(entry) {
    /**
     * If the inspector is not running, we ignore the event. This shouldn't be
     * possible in the current implementation, but we want to fail gracefully
     * because we don't want to cause any issues in the machine.
     *
     * @returns {boolean} Whether the event was added to the timeline
     */
    if (this.status !== 'running') {
      if (this.debug) {
        console.log('Inspector is not running, ignoring event', entry);
      }
      return false;
    }
    this.timeline.push(entry);
    this.timelineVersion += 1;
    if (this.timeline.length > this.maxTimelineSize) {
      this.timeline.shift();
    }
    return true;
  }

  /**
   * The timeline contains all events, decisions, errors, and transitions that
   * have occurred in the machine.
   *
   * @returns {MachineTimelineEntry<TEvent>[]} List of events in the timeline in the order they occurred
   */
  getTimeline() {
    return this.timeline;
  }
}